import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "@/firebase/app";
import { planGPSAssignments, type GPSPlannerDocument } from "@/models/gpsAssignmentPlanner";
import { stringValue } from "@/models/firestore";

// Port of FirestoreGPSAssignmentWriter.assignGPSID: one transaction re-reads
// every candidate device doc, runs the swap/backfill planner, applies only the
// gps-id diffs (rules: canAssignGPSTrackerFields allows a {"gps-id"} update),
// and advances gpsAssignmentState/default.nextGPSID monotonically.

export interface GPSAssignmentCandidate {
  id: string;
  /** Resolved for UI labels; the planner only needs it for the swap hint. */
  displayName: string;
}

export async function applyGPSIDAssignment(args: {
  instanceId: string;
  targetDocumentID: string;
  /** Normalized "001".."999". */
  desiredGPSID: string;
  /** Device ids + names from the mounted snapshot, in planner (list) order. */
  candidates: GPSAssignmentCandidate[];
}): Promise<void> {
  const { instanceId, targetDocumentID, desiredGPSID, candidates } = args;
  const stateRef = doc(db, "leagueCompetitionInstances", instanceId, "gpsAssignmentState", "default");

  await runTransaction(db, async (transaction) => {
    // All reads first (transaction requirement). Web transactions cannot
    // query, so the candidate list comes from the live snapshot.
    const documents: GPSPlannerDocument[] = [];
    const references = new Map<string, ReturnType<typeof doc>>();
    for (const candidate of candidates) {
      const reference = doc(db, "leagueCompetitionInstances", instanceId, "gpsData", candidate.id);
      const snapshot = await transaction.get(reference);
      if (!snapshot.exists()) continue;
      const data = snapshot.data();
      // iOS parity: only devices that have reported a location participate.
      if (!Array.isArray(data.locations) || data.locations.length === 0) continue;
      references.set(candidate.id, reference);
      documents.push({
        id: candidate.id,
        displayName: candidate.displayName,
        gpsID: stringValue(data["gps-id"]),
      });
    }

    const stateSnapshot = await transaction.get(stateRef);
    const existingNext =
      stateSnapshot.exists() && typeof stateSnapshot.data().nextGPSID === "number"
        ? (stateSnapshot.data().nextGPSID as number)
        : 1;

    const plan = planGPSAssignments(documents, targetDocumentID, desiredGPSID);

    for (const [id, gpsID] of plan.changed) {
      transaction.update(references.get(id)!, { "gps-id": gpsID });
    }
    transaction.set(
      stateRef,
      // max() keeps the rules' never-decreasing nextGPSID constraint.
      { id: "default", nextGPSID: Math.max(existingNext, plan.nextGPSID), updatedAt: serverTimestamp() },
      { merge: true },
    );
  });
}
